import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import CompanyService from '../services/companyService';
import AuthService from '../services/authService';
import { getStoredSelectedCompany } from '../utils/ssoHandoff';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

const restoreSelectedCompany = (companyList) => {
  const savedCompany = getStoredSelectedCompany();
  if (!savedCompany?.id) return null;

  if (companyList.length > 0) {
    const found = companyList.find(
      (c) => String(c.id) === String(savedCompany.id) || c.id === savedCompany.id
    );
    return found || savedCompany;
  }

  return savedCompany;
};

export const CompanyProvider = ({ children }) => {
  const [userId, setUserId] = useState(() => AuthService.getCurrentUser()?.id ?? null);
  const [selectedCompany, setSelectedCompany] = useState(() => getStoredSelectedCompany());
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const loadCompanies = useCallback(async () => {
    const uid = AuthService.getCurrentUser()?.id;
    if (!uid) {
      setCompanies([]);
      setSelectedCompany(null);
      localStorage.removeItem('selectedCompany');
      localStorage.removeItem('selectedCompanyId');
      return;
    }

    setLoadingCompanies(true);
    try {
      const result = await CompanyService.getCompaniesByUserId(uid);
      if (result.success) {
        const companyList = result.data || [];
        setCompanies(companyList);
        setSelectedCompany((prev) => restoreSelectedCompany(companyList) ?? prev);
      }
    } catch (err) {
      console.error('Fetch companies error:', err);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    const onLogin = () => {
      setUserId(AuthService.getCurrentUser()?.id ?? null);
      const handedOff = getStoredSelectedCompany();
      if (handedOff?.id) setSelectedCompany(handedOff);
      loadCompanies();
    };

    const onLogout = () => {
      setUserId(null);
      setCompanies([]);
      setSelectedCompany(null);
      localStorage.removeItem('selectedCompany');
      localStorage.removeItem('selectedCompanyId');
    };

    window.addEventListener('userLogin', onLogin);
    window.addEventListener('userLogout', onLogout);
    return () => {
      window.removeEventListener('userLogin', onLogin);
      window.removeEventListener('userLogout', onLogout);
    };
  }, [loadCompanies]);

  useEffect(() => {
    if (userId) loadCompanies();
  }, [userId, loadCompanies]);

  useEffect(() => {
    if (selectedCompany?.id) {
      try {
        localStorage.setItem('selectedCompany', JSON.stringify(selectedCompany));
        localStorage.setItem('selectedCompanyId', String(selectedCompany.id));
        window.dispatchEvent(new CustomEvent('companyChanged', { detail: selectedCompany }));
      } catch (err) {
        console.error('Error saving company to localStorage:', err);
      }
    } else {
      localStorage.removeItem('selectedCompany');
      localStorage.removeItem('selectedCompanyId');
      window.dispatchEvent(new CustomEvent('companyChanged', { detail: null }));
    }
  }, [selectedCompany]);

  const value = {
    selectedCompany,
    setSelectedCompany,
    companies,
    loadingCompanies,
    companyId: selectedCompany?.id || null,
    taxId: selectedCompany?.vergiKimlikNo || null,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
